import { assignProperties } from '@attain-apps/object-utils'
import { format } from 'pretty-format'

import type { TypeGuard } from './type-guard'

const TYPE_CHECKER_SYMBOL = Symbol.for('@attain-apps.TypeChecker')

export type TypeChecker<T> = TypeChecker.As<TypeChecker.Function<T>>
export namespace TypeChecker {
  /**
   * This can be used to add type parameters to the type checking function
   *
   * @example
   * const isList: TypeChecker.Of<<T>(val: any) => val is T[]> = Array.isArray
   * isList<number>([ 1, 2, 3 ])
   */
  export type As<TFn extends Function<any>> = TFn &
    TypeGuard.Props & {
      /**
       * For some reason, intersection with function types cause weird behavior
       * for type inference, particularly when it comes to {@link Array.filter}.
       * This is a work-around for that use case.
       *
       * @example
       * const someNullishItems: (string | undefined)[] = []
       * const noNullishItems: string[] = someNullishItems.filter(TypeChecker.isNotNullish.raw)
       */
      readonly raw: TFn
      readonly usingAlias: (
        alias: string | (() => string),
      ) => TypeChecker.As<TFn>
    }

  export type Function<T> = (val: any) => val is T

  export type ExtractTypeFrom<TChecker extends TypeChecker<any>> =
    TChecker extends TypeChecker<infer T> ? T : never

  // TODO: JSDocs needed
  export namespace Factory {
    export function custom<TFn extends Function<any>>(
      fn: TFn,
      props: TypeGuard.Props,
    ): TypeChecker.As<TFn> {
      return assignProperties(
        // Spreading args in case optional args as used by fn
        ((...args) => fn(...args)) as typeof fn,
        props,
        {
          [TYPE_CHECKER_SYMBOL]: true,
          raw: fn,
          usingAlias: (alias: string | (() => string)) => {
            // NOTE: Object.create preserves the accessors on props
            const propsWithAlias = Object.create(props) as typeof props
            // Overwrites the typeDescription with the new alias
            Object.defineProperty(propsWithAlias, 'typeDescription', {
              get() {
                return typeof alias === 'string' ? alias : alias()
              },
            })
            return custom(fn, propsWithAlias)
          },
        },
      )
    }

    export function markerSymbol<T, TSymbol extends symbol>(
      symbol: TSymbol,
      description?: string,
    ): TypeChecker<T & MarkedBy<TSymbol>> {
      return custom(
        (val: unknown): val is T & MarkedBy<TSymbol> =>
          !!val && !!val[symbol as keyof typeof val],
        {
          typeDescription: description ?? symbol.description ?? 'unknown',
        },
      )
    }

    export function instance<TClass extends abstract new (...args: any) => any>(
      _class: TClass,
    ): TypeChecker<InstanceType<TClass>> {
      return custom(
        (val: unknown): val is InstanceType<TClass> => val instanceof _class,
        { typeDescription: _class.name },
      )
    }

    export function optional<T>(
      typeChecker: TypeChecker<T>,
    ): TypeChecker<T | undefined> {
      return union(typeChecker, isNullish) as TypeChecker<T>
    }

    export function exact<const T>(
      target: T,
      isEqual?: (a: unknown, b: unknown) => boolean,
    ): TypeChecker<T> {
      return custom(
        (val: unknown): val is T =>
          isEqual ? isEqual(val, target) : val === target,
        { typeDescription: format(target) },
      )
    }

    export function oneOf<const TValues extends Iterable<any>>(
      values: TValues,
    ): TypeChecker<TValues extends Iterable<infer T> ? T : unknown>
    export function oneOf<const TValues extends readonly any[]>(
      ...values: TValues
    ): TypeChecker<TValues[number]>
    export function oneOf<const TValues extends readonly any[]>(
      ...values: TValues
    ): TypeChecker<TValues[number]> {
      const valuesArray =
        values.length === 1 && isIterable(values[0])
          ? Array.from(values[0])
          : values
      const valuesSet = new Set(valuesArray)
      const typeDescription = valuesArray.map(val => format(val)).join(' | ')
      return custom(
        (val: unknown): val is TValues[number] => valuesSet.has(val),
        {
          typeDescription,
        },
      )
    }

    export function list<T>(
      typeChecker: TypeChecker<T>,
    ): TypeChecker<readonly T[]>
    export function list<THead extends readonly unknown[], TRest>(params: {
      readonly startsWith: {
        readonly [K in keyof THead]: TypeChecker<THead[K]>
      }
      readonly endsWith: TypeChecker<TRest>
    }): TypeChecker<readonly [...THead, ...TRest[]]>
    export function list<TRest, TTail extends readonly unknown[]>(params: {
      readonly startsWith: TypeChecker<TRest>
      readonly endsWith: { readonly [K in keyof TTail]: TypeChecker<TTail[K]> }
    }): TypeChecker<readonly [...TRest[], ...TTail]>
    export function list(
      params:
        | TypeChecker<unknown>
        | {
            readonly startsWith:
              | TypeChecker<unknown>
              | readonly TypeChecker<unknown>[]
            readonly endsWith:
              | TypeChecker<unknown>
              | readonly TypeChecker<unknown>[]
          },
    ): TypeChecker<readonly unknown[]> {
      let checkItem: (val: unknown, i: number, len: number) => boolean
      let minLength = 0
      let typeDescriptGetter: () => string
      if (TypeChecker.isTypeChecker(params)) {
        // If we are given one type checker, we use it for every item

        // NOTE: we create a new arrow function so that we don't accidentally
        // pass i and len to the given type checker, as it may falsely interpret
        // those as some other optional arguments
        checkItem = val => params(val)
        typeDescriptGetter = () => `${params.typeDescription}[]`
      } else {
        const { endsWith, startsWith } = params
        if (TypeChecker.isTypeChecker(startsWith)) {
          if (TypeChecker.isTypeChecker(endsWith)) {
            // If we are given a type checker for startsWith and endsWith, we
            // simply check that every item meets both
            checkItem = val => startsWith(val) && endsWith(val)
            typeDescriptGetter = () =>
              `(${startsWith.typeDescription} & ${endsWith.typeDescription})[]`
          } else {
            // If we are given a type checker for startsWith and a list of type
            // checkers for endsWith, we check each item with the startsWith
            // type checker until the number of items remaining in the list is
            // less than or equal to the number of type checkers in endsWith, at
            // which point we check with the appropriate endsWith type checker
            // for each remaining item
            checkItem = (val, i, len) =>
              (len - i <= endsWith.length
                ? endsWith[endsWith.length - (len - i)]
                : startsWith)(val)
            // There must be at least as many items as there are endsWith type
            // checkers
            minLength = endsWith.length
            typeDescriptGetter = () =>
              `[...${startsWith.typeDescription}[], ${endsWith
                .map(endsWithChecker => endsWithChecker.typeDescription)
                .join(', ')}]`
          }
        } else if (TypeChecker.isTypeChecker(endsWith)) {
          // If we are given a list of type checkers for startsWith and a single
          // type checker for endsWith, we check each item with the appropriate
          // startsWith type checker until we get to the end of the list of
          // startsWith type checkers, at which point we check with the endsWith
          // type checker for each remaining items
          checkItem = (val, i, len) =>
            (i < startsWith.length ? startsWith[i] : endsWith)(val)
          // There must be at least as many items as there are startsWith type
          // checkers
          minLength = startsWith.length
          typeDescriptGetter = () =>
            `[${startsWith
              .map(startsWithChecker => startsWithChecker.typeDescription)
              .join(', ')}, ...${endsWith.typeDescription}[]]`
        } else {
          // If we are given lists of type checkers for both startsWith and
          // endsWith, we check that each item meets the appropriate startsWith
          // type checker, if there is one, and meets the appropriate endsWith
          // type checker, if there is one
          checkItem = (val, i, len) =>
            (i >= startsWith.length || startsWith[i](val)) &&
            (i < len - endsWith.length ||
              endsWith[endsWith.length - (len - i)](val))
          // There must be at least as many items as there are startsWith type
          // checkers, as well as endsWith type checkers. In other words, at
          // least as many as the greater of those two lengths
          minLength = Math.max(startsWith.length, endsWith.length)
          typeDescriptGetter = () =>
            `[${startsWith
              .map(startsWithChecker => startsWithChecker.typeDescription)
              .join(', ')}, ...unknown[]] & [...unknown[], ${endsWith
              .map(endsWithChecker => endsWithChecker.typeDescription)
              .join(', ')}]`
        }
      }
      return custom(
        (val: unknown): val is readonly unknown[] =>
          Array.isArray(val) &&
          val.length >= minLength &&
          val.every((v, i) => checkItem(v, i, val.length)),
        {
          get typeDescription(): string {
            return typeDescriptGetter()
          },
        },
      )
    }

    export function tuple<TTypeCheckers extends [...TypeChecker<any>[]]>(
      ...typeCheckers: TTypeCheckers
    ): TypeChecker<{
      [K in keyof TTypeCheckers]: TTypeCheckers[K] extends TypeChecker<infer T>
        ? T
        : unknown
    }> {
      return custom(
        (
          val: unknown,
        ): val is {
          [K in keyof TTypeCheckers]: TTypeCheckers[K] extends TypeChecker<
            infer T
          >
            ? T
            : unknown
        } =>
          Array.isArray(val) &&
          val.length === typeCheckers.length &&
          typeCheckers.every((typeChecker, i) => typeChecker(val[i])),
        {
          get typeDescription(): string {
            return `[ ${typeCheckers
              .map(typeChecker => typeChecker.typeDescription)
              .join(', ')} ]`
          },
        },
      )
    }

    export function set<T>(typeChecker: TypeChecker<T>): TypeChecker<Set<T>> {
      return custom(
        (val: unknown): val is Set<T> =>
          val instanceof Set && Array.from(val).every(v => typeChecker(v)),
        {
          get typeDescription(): string {
            return `Set<${typeChecker.typeDescription}>`
          },
        },
      )
    }

    /** Used to indicate keys to pick/omit */
    const SPECIAL_KEYS_SYMBOL = Symbol.for(
      'TypeChecker.Factory.object.SpecialKeys',
    )
    interface SpecialKeys {
      include?: Set<keyof any>
      exclude?: Set<keyof any>
    }
    namespace SpecialKeys {
      export interface PotentialHolder {
        [SPECIAL_KEYS_SYMBOL]?: SpecialKeys
      }
    }

    export function object<T extends object>(
      typeCheckers: object.Fields<T>,
    ): TypeChecker<T> {
      return custom(
        (val: unknown): val is T => {
          if (!isObject(val)) {
            return false
          }
          const specialKeys = (val as SpecialKeys.PotentialHolder)[
            SPECIAL_KEYS_SYMBOL
          ]
          for (const k in typeCheckers) {
            if (
              !typeCheckers[k as keyof typeof typeCheckers] ||
              !!specialKeys?.exclude?.has(k) ||
              (specialKeys?.include && !specialKeys.include.has(k))
            ) {
              continue
            }
            if (
              !typeCheckers[k as keyof typeof typeCheckers](
                val[k as keyof typeof val],
              )
            ) {
              return false
            }
          }
          return true
        },
        {
          get typeDescription(): string {
            const fieldNames = Object.keys(
              typeCheckers,
            ) as unknown[] as readonly (keyof typeof typeCheckers & string)[]
            const fields = fieldNames.map(
              fieldName =>
                `${
                  /^\w+$/.test(fieldName)
                    ? fieldName
                    : `'${fieldName.replace(/'/g, "\\'")}'`
                }: ${typeCheckers[fieldName].typeDescription}`,
            )
            const typeDescription =
              fields.length <= 3
                ? `{ ${fields.join(', ')} }`
                : `{\n\t${fields.join('\n\t')}\n}`
            return typeDescription
          },
        },
      )
    }
    export namespace object {
      export type Fields<T extends object> = {
        // Readonly allows TypeCheckers for readonly lists where mutable lists
        // are expected
        [K in keyof T]-?: TypeChecker<T[K]> | TypeChecker<Readonly<T[K]>>
      }

      export function partial<T extends object>(
        typeCheckers: object.Fields<T>,
      ): TypeChecker<T extends Partial<T> ? T : Partial<T>> {
        const partialTypeCheckers = {} as object.Fields<Partial<T>>
        for (const k in typeCheckers) {
          partialTypeCheckers[k] = optional(typeCheckers[k]) as object.Fields<
            Partial<T>
          >[typeof k]
        }
        return object(partialTypeCheckers) as TypeChecker<
          T extends Partial<T> ? T : Partial<T>
        >
      }
    }

    export function record<K extends keyof any, V>(
      keyTypeChecker: TypeChecker<K>,
      valueTypeChecker: TypeChecker<V>,
    ): TypeChecker<Record<K, V>> {
      return custom(
        (val: any): val is Record<K, V> =>
          isObject(val) &&
          Object.keys(val).every(
            k =>
              keyTypeChecker(k) && valueTypeChecker(val[k as keyof typeof val]),
          ),
        {
          get typeDescription(): string {
            return `Record<${keyTypeChecker.typeDescription}, ${valueTypeChecker.typeDescription}>`
          },
        },
      )
    }

    export function map<K, V>(
      keyTypeChecker: TypeChecker<K>,
      valueTypeChecker: TypeChecker<V>,
    ): TypeChecker<Map<K, V>> {
      return custom(
        (val: unknown): val is Map<K, V> =>
          val instanceof Map &&
          Array.from(val).every(
            ([k, v]) => keyTypeChecker(k) && valueTypeChecker(v),
          ),
        {
          get typeDescription(): string {
            return `Map<${keyTypeChecker.typeDescription}, ${valueTypeChecker.typeDescription}>`
          },
        },
      )
    }

    export function union(): TypeChecker<never>
    export function union<TTypeCheckers extends TypeChecker<any>[]>(
      ...typeCheckers: TTypeCheckers
    ): TypeChecker<TTypeCheckers extends TypeChecker<infer T>[] ? T : never>
    export function union(
      ...typeCheckers: readonly TypeChecker<unknown>[]
    ): TypeChecker<unknown> {
      switch (typeCheckers.length) {
        case 0:
          // Union of nothing = never
          return TypeChecker.isNever
        case 1:
          // Union of one type = that type
          return typeCheckers[0]
        default:
          // Union of multiple types needs custom logic
          return custom(
            (val: unknown): val is unknown =>
              typeCheckers.some(typeChecker => typeChecker(val)),
            {
              get typeDescription(): string {
                return typeCheckers
                  .map(typeChecker => typeChecker.typeDescription)
                  .join(' | ')
              },
            },
          )
      }
    }

    export function intersection(): TypeChecker<never>
    export function intersection<TTypeCheckers extends TypeChecker<any>[]>(
      ...typeCheckers: TTypeCheckers
    ): TypeChecker<IntersectionType<TTypeCheckers[number]>>
    export function intersection(
      ...typeCheckers: readonly TypeChecker<unknown>[]
    ): TypeChecker<unknown> {
      switch (typeCheckers.length) {
        case 0:
          // Intersection of nothing = never
          return TypeChecker.isNever
        case 1:
          // Intersection of one type = that type
          return typeCheckers[0]
        default:
          // Intersection of multiple types needs custom logic
          return custom(
            (val: unknown): val is unknown =>
              typeCheckers.every(typeChecker => typeChecker(val)),
            {
              get typeDescription(): string {
                return typeCheckers
                  .map(typeChecker => typeChecker.typeDescription)
                  .join(' & ')
              },
            },
          )
      }
    }
    /**
     * This is similar to `UnionToIntersection`, but distributes over
     * TypeCheckers rather than the checked types themselves to cover the case
     * where one of the TypeCheckers checks for a union. For example,
     * `TypeChecker.Factory.intersection(TypeChecker.Factory.union(...), ...)`
     */
    type IntersectionType<TTypeChecker extends TypeChecker<any>> = (
      TTypeChecker extends any ? (_: TTypeChecker) => void : never
    ) extends (_: TypeChecker<infer T>) => void
      ? T
      : never

    function helperForPickAndOmit<T>(
      typeChecker: TypeChecker<T>,
      // NOTE: need to be able to modify the special symbol

      val: SpecialKeys.PotentialHolder,
      keys: readonly (keyof T)[],
      prop: keyof SpecialKeys,
    ): boolean {
      if (!TypeChecker.isObject(val)) {
        return false
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      val = Object.create(val)
      const specialKeys: SpecialKeys = {
        [prop]: new Set(keys),
      }
      // If something higher up the chain already added special keys, we
      // need to incorporate those (without mutating them directly since
      // there may be parallel type checkers referencing those)
      if (val[SPECIAL_KEYS_SYMBOL]) {
        const existingSpecialKeys = val[SPECIAL_KEYS_SYMBOL]
        for (const rawK in existingSpecialKeys) {
          const k = rawK as keyof typeof existingSpecialKeys
          const existingSet = existingSpecialKeys[k]
          if (!existingSet) {
            continue
          }
          if (k === prop) {
            for (const key of existingSet.values()) {
              specialKeys[prop]?.add(key)
            }
          } else {
            specialKeys[k] = existingSet
          }
        }
      }
      val[SPECIAL_KEYS_SYMBOL] = specialKeys
      return typeChecker(val)
    }

    /**
     * __WARNING:__ this works by communicating with underlying {@link object}
     * type checkers. If the given type checker does not ultimately use a type
     * checker created with the {@link object} factory for checking the fields
     * of a given value, this will not work.
     */
    export function pick<T, K extends keyof T>(
      typeChecker: TypeChecker<T>,
      keys: readonly K[],
    ): TypeChecker<Pick<T, K>> {
      return custom(
        (val: SpecialKeys.PotentialHolder): val is Pick<T, K> =>
          helperForPickAndOmit(typeChecker, val, keys, 'include'),
        {
          get typeDescription(): string {
            return `Pick<${typeChecker.typeDescription}, ${keys
              .map(val => format(val))
              .join(' | ')}>`
          },
        },
      )
    }

    /**
     * __WARNING:__ this works by communicating with underlying `object` type
     * checkers. If the given type checker does not ultimately use a type
     * checker created with the `object` factory for checking the fields of a
     * given value, this will not work.
     */
    export function omit<T, K extends keyof T>(
      typeChecker: TypeChecker<T>,
      keys: readonly K[],
    ): TypeChecker<Omit<T, K>> {
      return custom(
        (val: SpecialKeys.PotentialHolder): val is Omit<T, K> =>
          helperForPickAndOmit(typeChecker, val, keys, 'exclude'),
        {
          get typeDescription(): string {
            return `Omit<${typeChecker.typeDescription}, ${keys
              .map(val => format(val))
              .join(' | ')}>`
          },
        },
      )
    }

    export function extract<T, TExtract>(
      mainTypeChecker: TypeChecker<T>,
      extractionTypeChecker: TypeChecker<TExtract>,
    ): TypeChecker<Extract<T, TExtract>> {
      return custom(
        (val: unknown): val is Extract<T, TExtract> =>
          mainTypeChecker(val) && extractionTypeChecker(val),
        {
          get typeDescription(): string {
            return `Extract<${mainTypeChecker.typeDescription}, ${extractionTypeChecker.typeDescription}>`
          },
        },
      )
    }

    export function exclude<T, TExclude>(
      mainTypeChecker: TypeChecker<T>,
      exclusionTypeChecker: TypeChecker<TExclude>,
    ): TypeChecker<Exclude<T, TExclude>> {
      return custom(
        (val: unknown): val is Exclude<T, TExclude> =>
          mainTypeChecker(val) && !exclusionTypeChecker(val),
        {
          get typeDescription(): string {
            return `Exclude<${mainTypeChecker.typeDescription}, ${exclusionTypeChecker.typeDescription}>`
          },
        },
      )
    }

    /**
     * If you need to reference something that is not yet defined, you can use
     * this factory method to defer the creation of the type checking function
     * to when it is first called
     *
     * @example
     * ```ts
     * export type X = Y | Z
     * namespace X {
     *   export const isX: TypeChecker<X> = TypeChecker.Factory.lazy(() => TypeChecker.Factory.union(Y.isY, Z.isZ))
     * }
     *
     * export interface Y { z: Z }
     * namespace Y {
     *   export const isY = TypeChecker.Factory.lazy(() => TypeChecker.Factory.object<Y>({ z: Z.isZ }))
     * }
     *
     * export type Z = string
     * namespace Z {
     *   export const isZ: TypeChecker<Z> = TypeChecker.isString
     * }
     * ```
     */
    export function lazy<TTypeChecker extends TypeChecker<any>>(
      producer: () => TTypeChecker,
    ): TypeChecker<TTypeChecker extends TypeChecker<infer T> ? T : unknown> {
      let typeChecker: TTypeChecker
      return custom(
        (
          val: unknown,
        ): val is TTypeChecker extends TypeChecker<infer T> ? T : unknown =>
          (typeChecker ??= producer())(val),
        {
          get typeDescription(): string {
            return (
              typeChecker?.typeDescription ??
              'unknown (lazy type checker not yet called)'
            )
          },
        },
      )
    }
  }

  export const isTypeChecker: TypeChecker<TypeChecker<unknown>> =
    TypeChecker.Factory.markerSymbol(TYPE_CHECKER_SYMBOL)

  export const isString = Factory.custom(
    (val: unknown): val is string => typeof val === 'string',
    { typeDescription: 'string' },
  )
  export const isNumber = Factory.custom(
    (val: unknown): val is number => typeof val === 'number',
    { typeDescription: 'number' },
  )
  export const isBigInt = Factory.custom(
    (val: unknown): val is bigint => typeof val === 'bigint',
    { typeDescription: 'bigint' },
  )
  export const isBoolean = Factory.custom(
    (val: unknown): val is boolean => typeof val === 'boolean',
    { typeDescription: 'boolean' },
  )
  export const isTrue = Factory.custom(
    (val: unknown): val is true => val === true,
    { typeDescription: 'true' },
  )
  export const isFalse = Factory.custom(
    (val: unknown): val is false => val === false,
    { typeDescription: 'false' },
  )
  export const isFalsish = Factory.custom(
    (val: unknown): val is Falsish => !val,
    { typeDescription: 'Falsish' },
  )
  export const isNotFalsish = Factory.custom(
    <T>(val: T | Falsish): val is T => !isFalsish(val),
    // NOTE: this is not a real type
    { typeDescription: 'Exclude<any, Falsish>' },
  )
  export const isSymbol = Factory.custom(
    (val: unknown): val is symbol => typeof val === 'symbol',
    { typeDescription: 'symbol' },
  )
  /**
   * __NOTE:__ even though `typeof null === 'object'`, this returns `false` for
   * `null`. Other than that, this is identical to doing `typeof value ===
   * 'object'`
   */
  export const isObject = Factory.custom(
    (val: unknown): val is object => val !== null && typeof val === 'object',
    { typeDescription: 'object' },
  )
  export const isFunction = Factory.custom(
    (val: unknown): val is globalThis.Function => typeof val === 'function',
    { typeDescription: 'function' },
  )
  export const isDate = Factory.custom(
    (val: unknown): val is Date => val instanceof Date,
    { typeDescription: 'Date' },
  )
  export const isRegExp = Factory.custom(
    (val: unknown): val is RegExp => val instanceof RegExp,
    { typeDescription: 'RegExp' },
  )
  export const isUrl = Factory.custom(
    (val: unknown): val is URL => val instanceof URL,
    { typeDescription: 'URL' },
  )
  export const isIterable = Factory.custom(
    (val: unknown): val is Iterable<unknown> =>
      isObject(val) &&
      typeof (val as Iterable<unknown>)[Symbol.iterator] === 'function',
    { typeDescription: 'Iterable' },
  )
  export const isIterator = Factory.custom(
    (val: unknown): val is Iterator<unknown> =>
      isObject(val) && typeof (val as Iterator<unknown>).next === 'function',
    { typeDescription: 'Iterator' },
  )
  export const isIterableIterator = Factory.custom(
    (val: unknown): val is IterableIterator<unknown> =>
      isIterable(val) && isIterator(val),
    { typeDescription: 'IterableIterator' },
  )
  export const isArray = Factory.custom(Array.isArray, {
    typeDescription: 'any[]',
  })
  /**
   * Same as {@link isArray}, but typed to allow readonly arrays
   */
  export const isReadonlyArray: TypeChecker<readonly unknown[]> =
    isArray.usingAlias('readonly unknown[]')
  export const isSet = Factory.instance(Set).usingAlias('Set<unknown>')
  /**
   * Same as {@link isSet}, but typed to allow {@link ReadonlySet}
   */
  export const isReadonlySet: TypeChecker<ReadonlySet<unknown>> =
    isSet.usingAlias('ReadonlySet<unknown>')
  export const isNull = Factory.custom(
    (val: unknown): val is null => val === null,
    { typeDescription: 'null' },
  )
  export const isUndefined = Factory.custom(
    (val: unknown): val is undefined => val === undefined,
    { typeDescription: 'undefined' },
  )
  export const isVoid = Factory.custom(
    (val: unknown): val is void => val === undefined,
    { typeDescription: 'void' },
  )
  export const isNullish = Factory.custom(
    (val: unknown): val is Nullish => val === null || val === undefined,
    { typeDescription: 'null | undefined | void' },
  )
  export const isNotNullish = Factory.custom(
    <T>(val: T | Nullish): val is T => !isNullish(val),
    { typeDescription: 'NonNullable<unknown>' },
  )
  export const isNever = Factory.custom((val: unknown): val is never => false, {
    typeDescription: 'never',
  })
  export const isAny = Factory.custom((val: unknown): val is any => true, {
    typeDescription: 'any',
  })
  export const isUnknown = Factory.custom(
    (val: unknown): val is unknown => true,
    {
      typeDescription: 'unknown',
    },
  )
  export const isPrimitive: TypeChecker<Primitive> = Factory.union(
    isString,
    isNumber,
    isBigInt,
    isBoolean,
    isSymbol,
    isNull,
    isUndefined,
  )

  /** Checks if {@link PromiseSettledResult} is {@link PromiseFulfilledResult} */
  export const isPromiseFulfilledResult = Factory.custom(
    <T>(
      val: Readonly<PromiseSettledResult<T>>,
    ): val is PromiseFulfilledResult<T> => val.status === 'fulfilled',
    { typeDescription: 'unknown' },
  )
  /** Checks if {@link PromiseSettledResult} is {@link PromiseRejectedResult} */
  export const isPromiseRejectedResult = Factory.custom(
    (val: Readonly<PromiseSettledResult<any>>): val is PromiseRejectedResult =>
      val.status === 'rejected',
    { typeDescription: 'unknown' },
  )
}
